import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { adminUsers, auditLogsEndpoints } from '@ooh/contracts';
import type { Role } from '@ooh/contracts';
import { useSuspenseQueries } from '@tanstack/react-query';
import { apiQueryOptions } from '../../data/use-api-suspense-query';
import { useApiClient } from '../../plugs/api-client';
import { QueryBoundary } from '../../layouts/query-boundary';
import { AuditEntryList, AuditHistoryLoading, AuditHistoryPanel } from './audit-history-panel';
import { formatAuditEntries } from './format-audit-entry';

type AuditActionLabelKey =
  | 'permissionMatrix.auditPanel.actions.roleCreated'
  | 'permissionMatrix.auditPanel.actions.roleUpdated'
  | 'permissionMatrix.auditPanel.actions.roleScopesUpdated';

/** Server audit `action` values (see `apps/api/src/roles/roles.service.ts`) -> i18n key. */
const AUDIT_ACTION_LABEL_KEYS: Record<string, AuditActionLabelKey> = {
  'role.created': 'permissionMatrix.auditPanel.actions.roleCreated',
  'role.updated': 'permissionMatrix.auditPanel.actions.roleUpdated',
  'role.permissions_updated': 'permissionMatrix.auditPanel.actions.roleScopesUpdated',
};

function RoleAuditEntries({ role }: { role: Role }) {
  const { t } = useTranslation('admin');
  const api = useApiClient();
  const [{ data: auditLog }, { data: users }] = useSuspenseQueries({
    queries: [
      apiQueryOptions(api, auditLogsEndpoints.list, {
        entityType: 'role',
        entityId: role.id,
        page: 1,
        pageSize: 25,
      }),
      apiQueryOptions(api, adminUsers.listUsers, { page: 1, pageSize: 200 }),
    ],
  });

  const entries = useMemo(() => {
    const userNamesById = new Map(users.items.map((u) => [u.id, u.name]));
    const auditActionLabel = (action: string): string => {
      const key = AUDIT_ACTION_LABEL_KEYS[action];
      return key ? t(key) : action;
    };
    return formatAuditEntries(
      auditLog.items,
      userNamesById,
      role.name,
      auditActionLabel,
      t('permissionMatrix.auditPanel.unknownActor'),
    );
  }, [auditLog, users, role.name, t]);

  return <AuditEntryList entries={entries} />;
}

interface RoleAuditHistoryPanelProps {
  role: Role | null;
  open: boolean;
  onClose: () => void;
}

/** Fetches the audit entries and user names only while the panel is open. */
export function RoleAuditHistoryPanel({ role, open, onClose }: RoleAuditHistoryPanelProps) {
  return (
    <AuditHistoryPanel open={open} onClose={onClose}>
      {open && role ? (
        <QueryBoundary fallback={<AuditHistoryLoading />}>
          <RoleAuditEntries role={role} />
        </QueryBoundary>
      ) : null}
    </AuditHistoryPanel>
  );
}
